import { useId, useRef, useState } from "react";
import { useT } from "../i18n/index.tsx";

type Props = {
  value: string;
  onChange: (v: string) => void;
  /** Called when the 6th digit is entered (login / enrolment auto-submit). */
  onComplete?: (v: string) => void;
  label?: string;
  error?: string | null;
  disabled?: boolean;
  autoFocus?: boolean;
  hint?: string;
};

/**
 * 6-digit authenticator code. One real <input> (so paste, iOS/Android one-time-code autofill and
 * screen readers just work) laid over six visual boxes.
 */
export function CodeInput({ value, onChange, onComplete, label, error, disabled, autoFocus, hint }: Props) {
  const t = useT();
  const id = useId();
  const ref = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const digits = value.padEnd(6, " ").slice(0, 6).split("");
  const active = Math.min(value.length, 5);
  return (
    <div className={`field code-field ${error ? "has-error" : ""}`}>
      <label htmlFor={id}>{label ?? t.auth.authenticatorCode}</label>
      <div className={`otp ${focused ? "focused" : ""} ${disabled ? "disabled" : ""}`} onMouseDown={(e) => { e.preventDefault(); ref.current?.focus(); }}>
        {digits.map((d, i) => (
          <span key={i} className={`otp-box ${focused && i === active ? "active" : ""} ${d.trim() ? "filled" : ""}`} aria-hidden="true">
            {d.trim()}
            {focused && i === active && !d.trim() && <i className="otp-caret" />}
          </span>
        ))}
        <input
          ref={ref}
          id={id}
          className="otp-input"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={6}
          value={value}
          disabled={disabled}
          data-autofocus={autoFocus ? "" : undefined}
          autoFocus={autoFocus}
          aria-invalid={!!error}
          aria-describedby={error || hint ? `${id}-msg` : undefined}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, "").slice(0, 6);
            onChange(v);
            if (v.length === 6 && v !== value) onComplete?.(v);
          }}
          onSelect={(e) => {
            const el = e.currentTarget;
            const end = el.value.length;
            if (el.selectionStart !== end || el.selectionEnd !== end) el.setSelectionRange(end, end);
          }}
        />
      </div>
      {(error || hint) && (
        <p id={`${id}-msg`} className={error ? "field-error" : "field-hint"}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
}
